import { useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api, RequestOptions, ApiError, getStoredCurrentOrganizationId } from '../utils/api';
import { ImpersonationManager } from '../utils/impersonationManager';
import toast from '../design-system/toast';

/**
 * Hook that provides an API client with automatic authentication
 * - Includes JWT token and impersonation headers when active
 */
// Track last workspace access error to prevent duplicate notifications
let lastWorkspaceAccessError = 0;
const WORKSPACE_ACCESS_ERROR_COOLDOWN = 2000; // 2 seconds

export const useAuthenticatedApi = () => {
  const { getAccessToken, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  // Use ref to avoid navigate causing useMemo to recreate the api object on every navigation
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  /**
   * Handle API errors and redirect on workspace access denied
   * Implements cooldown to prevent duplicate notifications from simultaneous failed requests
   * Redirects to /repositories on access denied errors
   */
  const handleApiError = useCallback((error: any) => {
    // Aborted requests during unmount (e.g. on logout) are expected, not errors.
    if (error?.name === 'AbortError') return;
    console.error('useAuthenticatedApi handleApiError', error);
    if (error instanceof ApiError && error.status === 403) {
      const message = error.message || '';
      // Check if this is a workspace access denial (template requirements)
      if (message.includes('Access denied:')) {
        const now = Date.now();
        
        // Only show notification and redirect if cooldown period has passed
        if (now - lastWorkspaceAccessError > WORKSPACE_ACCESS_ERROR_COOLDOWN) {
          lastWorkspaceAccessError = now;
          toast.error(message);
          // Redirect to applications on access denied
          navigateRef.current('/repositories', { replace: true });
        }
        
        return; // Don't re-throw after handling
      }
    }
    // Re-throw all other errors
    throw error;
  }, []);


  return useMemo(() => ({
    /**
     * Make a GET request with automatic token injection and impersonation
     */
    get: async <T = any>(endpoint: string, options: RequestInit = {}): Promise<T> => {
      try {
        const currentToken = await getAccessToken();
        const requestOptions: RequestOptions = { ...options, token: currentToken, userId: user?.id };

        // Add impersonation header if currently impersonating
        const impersonationUserId = ImpersonationManager.getCurrentImpersonationUserId();
        if (impersonationUserId) {
          requestOptions.impersonateUserId = impersonationUserId;
        }

        return await api.get<T>(endpoint, requestOptions);
      } catch (error) {
        handleApiError(error);
        throw error; // This won't be reached if handleApiError navigates
      }
    },

    /**
     * Make a GET request for a binary/file response with automatic auth headers.
     */
    getBlob: async (endpoint: string, options: RequestInit = {}): Promise<Blob> => {
      try {
        const currentToken = await getAccessToken();
        const requestOptions: RequestOptions = { ...options, token: currentToken, userId: user?.id };

        const impersonationUserId = ImpersonationManager.getCurrentImpersonationUserId();
        if (impersonationUserId) {
          requestOptions.impersonateUserId = impersonationUserId;
        }

        return await api.getBlob(endpoint, requestOptions);
      } catch (error) {
        handleApiError(error);
        throw error;
      }
    },

    /**
     * Make a POST request with automatic token injection and impersonation
     */
    post: async <T = any>(endpoint: string, data?: any, options: RequestInit = {}): Promise<T> => {
      try {
        const currentToken = await getAccessToken();
        const requestOptions: RequestOptions = { ...options, token: currentToken, userId: user?.id };

        // Add impersonation header if currently impersonating
        const impersonationUserId = ImpersonationManager.getCurrentImpersonationUserId();
        if (impersonationUserId) {
          requestOptions.impersonateUserId = impersonationUserId;
        }

        return await api.post<T>(endpoint, data, requestOptions);
      } catch (error) {
        handleApiError(error);
        throw error;
      }
    },

    /**
     * Make a PUT request with automatic token injection and impersonation
     */
    put: async <T = any>(endpoint: string, data?: any, options: RequestInit = {}): Promise<T> => {
      try {
        const currentToken = await getAccessToken();
        const requestOptions: RequestOptions = { ...options, token: currentToken, userId: user?.id };

        // Add impersonation header if currently impersonating
        const impersonationUserId = ImpersonationManager.getCurrentImpersonationUserId();
        if (impersonationUserId) {
          requestOptions.impersonateUserId = impersonationUserId;
        }

        return await api.put<T>(endpoint, data, requestOptions);
      } catch (error) {
        handleApiError(error);
        throw error;
      }
    },

    /**
     * Make a DELETE request with automatic token injection and impersonation
     */
    delete: async <T = any>(endpoint: string, options: RequestInit = {}): Promise<T> => {
      try {
        const currentToken = await getAccessToken();
        const requestOptions: RequestOptions = { ...options, token: currentToken, userId: user?.id };

        // Add impersonation header if currently impersonating
        const impersonationUserId = ImpersonationManager.getCurrentImpersonationUserId();
        if (impersonationUserId) {
          requestOptions.impersonateUserId = impersonationUserId;
        }

        return await api.delete<T>(endpoint, requestOptions);
      } catch (error) {
        handleApiError(error);
        throw error;
      }
    },

    /**
     * Make a PATCH request with automatic token injection and impersonation
     */
    patch: async <T = any>(endpoint: string, data?: any, options: RequestInit = {}): Promise<T> => {
      try {
        const currentToken = await getAccessToken();
        const requestOptions: RequestOptions = { ...options, token: currentToken, userId: user?.id };

        // Add impersonation header if currently impersonating
        const impersonationUserId = ImpersonationManager.getCurrentImpersonationUserId();
        if (impersonationUserId) {
          requestOptions.impersonateUserId = impersonationUserId;
        }

        return await api.patch<T>(endpoint, data, requestOptions);
      } catch (error) {
        handleApiError(error);
        throw error;
      }
    },

    uploadFile: async (endpoint: string, formData: FormData, options: RequestInit = {}) => {
        const currentToken = await getAccessToken();

        const requestOptions: RequestInit = {
          ...options,
          method: 'POST',
          headers: {
            Authorization: `Bearer ${currentToken}`,
          },
          body: formData,
        };

        // Add impersonation header if currently impersonating
        const impersonationUserId = ImpersonationManager.getCurrentImpersonationUserId();
        if (impersonationUserId) {
          requestOptions.headers = {
            ...requestOptions.headers,
            'X-Impersonate-User-ID': impersonationUserId,
          };
        }

        const orgId = getStoredCurrentOrganizationId(user?.id);
        if (orgId) {
          requestOptions.headers = {
            ...requestOptions.headers,
            'X-Organization-Id': orgId,
          };
        }

        const response = await fetch(`${api.getBaseUrl()}${endpoint}`, requestOptions);
        const json = await response.json();

        if (response.ok) {
          return json;
        }

        throw new Error(json.detail || 'Unknown error');
      },

      /**
       * Get the base URL for the API
       */
      getBaseUrl: () => api.getBaseUrl(),

      /**
       * Get the current auth token
       */
      getToken: async (): Promise<string | null> => {
        return await getAccessToken();
      },

      /**
       * Make an admin request that never impersonates (for admin-only operations)
       */
      adminRequest: async <T = any>(
        method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
        endpoint: string,
        data?: any,
        options: RequestInit = {}
      ): Promise<T> => {
        const currentToken = await getAccessToken();
        const requestOptions: RequestOptions = { ...options, token: currentToken, userId: user?.id };
        // Explicitly don't add impersonation headers for admin requests

        switch (method) {
          case 'GET':
            return api.get<T>(endpoint, requestOptions);
          case 'POST':
            return api.post<T>(endpoint, data, requestOptions);
          case 'PUT':
            return api.put<T>(endpoint, data, requestOptions);
          case 'DELETE':
            return api.delete<T>(endpoint, requestOptions);
          case 'PATCH':
            return api.patch<T>(endpoint, data, requestOptions);
          default:
            throw new Error(`Unsupported method: ${method}`);
        }
      },

      /**
       * Check if user is authenticated
       */
      isAuthenticated,

      /**
       * Get current impersonation state
       */
      getImpersonationState: () => ImpersonationManager.getCurrentState(),
  }), [getAccessToken, isAuthenticated, handleApiError, user?.id]);
};

export default useAuthenticatedApi;
